import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import CurrencyInput from '../common/CurrencyInput';
import { formatCurrency } from '../../utils/formatters';
import { calculateLumpSumImpact } from '../../utils/calculations';
import { LumpSumComparison } from '../../types/calculator.types';

const BondRepaymentLumpSum: React.FC = () => {
  const [loanAmount, setLoanAmount] = useState<number>(0);
  const [interestRate, setInterestRate] = useState<number>(10);
  const [loanTerm, setLoanTerm] = useState<number>(10);
  const [lumpSumAmount, setLumpSumAmount] = useState<number>(0);
  const [result, setResult] = useState<LumpSumComparison | null>(null);

  useEffect(() => {
    if (loanAmount > 0 && interestRate > 0 && loanTerm > 0 && lumpSumAmount > 0 && lumpSumAmount < loanAmount) {
      const comparison = calculateLumpSumImpact(loanAmount, interestRate, loanTerm, lumpSumAmount);
      setResult(comparison);
    } else {
      setResult(null);
    }
  }, [loanAmount, interestRate, loanTerm, lumpSumAmount]);

  return (
    <div className="p-2">
      <CurrencyInput
        value={loanAmount}
        onChange={setLoanAmount}
        label="Loan Amount (R)"
        required
      />

      <Form.Group className="mb-2">
        <div className="form-group has-float-label">
          <Form.Control
            type="number"
            min="0.1"
            max="50"
            step="0.1"
            value={interestRate}
            onChange={(e) => setInterestRate(parseFloat(e.target.value) || 10)}
            required
            placeholder=" "
          />
          <span className="required">Annual Interest Rate (%)</span>
        </div>
      </Form.Group>

      <Form.Group className="mb-2">
        <div className="form-group has-float-label">
          <Form.Control
            type="number"
            min="10"
            max="30"
            step="1"
            value={loanTerm}
            onChange={(e) => setLoanTerm(parseInt(e.target.value) || 10)}
            required
            placeholder=" "
          />
          <span className="required">Loan Term (Years)</span>
        </div>
      </Form.Group>

      <CurrencyInput
        value={lumpSumAmount}
        onChange={setLumpSumAmount}
        label="Lump Sum (R)"
        required
      />

      {result && (
        <table className="table table-sm mt-2" style={{ fontSize: '13px' }}>
          <thead>
            <tr>
              <th colSpan={2} className="text-secondary border-0 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                CALCULATED
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-0 py-1">Lump Installments on</td>
              <td className="text-end font-weight-bold border-0 py-1">R {formatCurrency(loanAmount)}</td>
            </tr>

            <tr>
              <th colSpan={2} className="text-secondary border-0 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                ORIGINAL PAYMENT
              </th>
            </tr>
            <tr>
              <td className="border-0 py-1">Pay-off time</td>
              <td className="text-end font-weight-bold border-0 py-1">{result.originalPayOffTime} years</td>
            </tr>
            <tr>
              <td className="py-1">Monthly payment</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.originalMonthlyPayment)}</td>
            </tr>
            <tr>
              <td className="py-1">Total capital paid</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.originalTotalCapitalPaid)}</td>
            </tr>
            <tr>
              <td className="py-1">Total interest paid</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.originalTotalInterestPaid)}</td>
            </tr>
            <tr>
              <td className="bg-light border-0 py-1">Total amount paid</td>
              <td className="text-end bg-light font-weight-bold border-0 py-1">
                R {formatCurrency(result.originalTotalAmountPaid)}
              </td>
            </tr>

            <tr>
              <td className="border-0 py-1"></td>
              <td className="border-0 py-1"></td>
            </tr>

            <tr>
              <th colSpan={2} className="text-secondary border-0 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                LUMP INSTALLMENTS
              </th>
            </tr>
            <tr>
              <td className="border-0 py-1">Pay-off time</td>
              <td className="text-end font-weight-bold border-0 py-1">{result.lumpPayOffTime}</td>
            </tr>
            <tr>
              <td className="py-1">Monthly payment</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.lumpMonthlyPayment)}</td>
            </tr>
            <tr>
              <td className="py-1">Total capital paid</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.lumpTotalCapitalPaid)}</td>
            </tr>
            <tr>
              <td className="py-1">Total interest paid</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.lumpTotalInterestPaid)}</td>
            </tr>
            <tr>
              <td className="bg-light border-0 py-1">Total amount paid</td>
              <td className="bg-light border-0 text-end font-weight-bold py-1">
                R {formatCurrency(result.lumpTotalAmountPaid)}
              </td>
            </tr>

            <tr>
              <td className="border-0 py-1"></td>
              <td className="border-0 py-1"></td>
            </tr>

            <tr>
              <th colSpan={2} className="text-secondary border-0 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                LUMP SAVINGS
              </th>
            </tr>
            <tr>
              <td className="border-0 text-success font-weight-bold py-1">Time saved</td>
              <td className="text-end text-success font-weight-bold border-0 py-1">{result.timeSaved}</td>
            </tr>
            <tr>
              <td className="text-success font-weight-bold py-1">Interest saved</td>
              <td className="text-end font-weight-bold text-success py-1">
                R {formatCurrency(result.interestSaved)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BondRepaymentLumpSum;
